import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';
import DocViewModal from '../components/DocViewModal';

const fmt = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (s) => { if (!s) return '—'; const d = new Date(s + 'T00:00:00'); return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); };

function Accountdetails() {
    const { id } = useParams(); // Get the account ID from URL
    const navigate = useNavigate();
    const [account, setAccount] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [interactions, setInteractions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showInteractionForm, setShowInteractionForm] = useState(false);
    const [interactionFormData, setInteractionFormData] = useState({
        interaction_type: '',
        date: new Date().toISOString().split('T')[0], // Default to current date
        subject: '',
        notes: '',
        next_followup_date: ''
    });
    const [submittingInteraction, setSubmittingInteraction] = useState(false);

    // Finance docs (quotations / invoices)
    const [viewingDoc,  setViewingDoc]  = useState(null);
    const [quotations, setQuotations] = useState([]);
    const [invoices,   setInvoices]   = useState([]);
    const paymentFormRef = React.useRef(null);

    // Payment records
    const [payments,         setPayments]         = useState([]);
    const [showPaymentForm,  setShowPaymentForm]  = useState(false);
    const [paymentForm,      setPaymentForm]      = useState({ date: new Date().toISOString().split('T')[0], amount: '', payment_method: 'UPI', reference_no: '', notes: '', quotation_id: '' });
    const [savingPayment,    setSavingPayment]    = useState(false);

    // Fetch specific account data
    const fetchAccountDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axiosInstance.get(`/accounts/details/${id}`);
            console.log("Account details response:", response.data);
            setAccount(response.data.data || response.data);
        } catch (error) {
            console.error("Error fetching account details:", error);
            setError("Failed to fetch account details");
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to fetch account details',
                confirmButtonText: 'Go Back'
            }).then(() => {
                navigate('/account'); // Navigate back to accounts list
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch associated contacts
    const fetchAccountContacts = async () => {
        try {
            const response = await axiosInstance.get(`/accounts/${id}/contacts`);
            console.log("Account contacts response:", response.data);
            setContacts(response.data.data || response.data || []);
        } catch (error) {
            console.error("Error fetching account contacts:", error);
            // Don't show error for contacts as it's not critical
        }
    };

    // Fetch account interactions
    const fetchAccountInteractions = async () => {
        try {
            const response = await axiosInstance.get(`/accountInteractions/account/${id}`);
            console.log("Account interactions response:", response.data);
            setInteractions(response.data.data || response.data || []);
        } catch (error) {
            console.error("Error fetching account interactions:", error);
            // Don't show error for interactions as it's not critical
        }
    };

    // Fetch finance documents by type — each call is independent so one failure doesn't blank the others
    const fetchDocs = async () => {
        const safe = async (type) => {
            try {
                const res = await axiosInstance.get(`/quotations?account_id=${id}&type=${type}`);
                return res.data.data || [];
            } catch (e) {
                console.error(`fetchDocs(${type}) failed:`, e?.response?.data?.error || e.message);
                return [];
            }
        };
        const [q, i] = await Promise.all([safe('quotation'), safe('invoice')]);
        setQuotations(q);
        setInvoices(i);
    };

    // Fetch payment records
    const fetchPayments = async () => {
        try {
            const res = await axiosInstance.get(`/account-payments?account_id=${id}`);
            setPayments(res.data.data || []);
        } catch {
            setPayments([]);
        }
    };

    // Save payment record
    const savePayment = async (e) => {
        e.preventDefault();
        if (!paymentForm.amount || !paymentForm.date) {
            Swal.fire({ icon: 'warning', title: 'Required', text: 'Amount and Date are required' });
            return;
        }
        setSavingPayment(true);
        try {
            await axiosInstance.post('/account-payments', {
                account_id:     id,
                amount:         paymentForm.amount,
                date:           paymentForm.date,
                payment_method: paymentForm.payment_method,
                reference_no:   paymentForm.reference_no || null,
                notes:          paymentForm.notes        || null,
                quotation_id:   paymentForm.quotation_id || null,
            });
            Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Payment saved!', showConfirmButton: false, timer: 2000 });
            setPaymentForm({ date: new Date().toISOString().split('T')[0], amount: '', payment_method: 'UPI', reference_no: '', notes: '', quotation_id: '' });
            setShowPaymentForm(false);
            fetchPayments();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.error || err.message });
        } finally {
            setSavingPayment(false);
        }
    };

    // Delete payment record
    const deletePayment = async (pid) => {
        const res = await Swal.fire({ title: 'Delete payment?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444', confirmButtonText: 'Delete' });
        if (!res.isConfirmed) return;
        try {
            await axiosInstance.delete(`/account-payments/${pid}`);
            fetchPayments();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.error || err.message });
        }
    };

    // Handle interaction form input changes
    const handleInteractionInputChange = (e) => {
        const { name, value } = e.target;
        setInteractionFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Submit new interaction
    const handleSubmitInteraction = async (e) => {
        e.preventDefault();
        
        if (!interactionFormData.interaction_type || !interactionFormData.date || !interactionFormData.subject) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Fields',
                text: 'Please fill in all required fields (Interaction Type, Date, and Subject)'
            });
            return;
        }

        setSubmittingInteraction(true);
        
        try {
            // Get user_id from localStorage
            let userId;
            try {
                // Try to get user object first
                const userData = localStorage.getItem('user');
                if (userData) {
                    const parsedUser = JSON.parse(userData);
                    userId = parsedUser.id;
                } else {
                    // Fallback to direct user_id
                    userId = localStorage.getItem('user_id');
                }
            } catch (error) {
                // If parsing fails, try getting user_id directly
                userId = localStorage.getItem('user_id');
            }

            if (!userId) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'User information not found. Please login again.'
                });
                setSubmittingInteraction(false);
                return;
            }

            const interactionData = {
                interaction_type: interactionFormData.interaction_type,
                date: interactionFormData.date,
                subject: interactionFormData.subject.trim(),
                note: interactionFormData.notes.trim() || null,
                next_followup_at: interactionFormData.next_followup_date || null,
                account_id: id,
                created_by: userId
            };

            // Remove null/empty values to avoid backend issues
            Object.keys(interactionData).forEach(key => {
                if (interactionData[key] === null || interactionData[key] === '') {
                    delete interactionData[key];
                }
            });

            console.log('Submitting interaction data:', interactionData);
            console.log('Form data before processing:', interactionFormData);

            const response = await axiosInstance.post('/accountInteractions', interactionData);
            
            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Account interaction added successfully'
                });
                
                // Reset form and refresh interactions
                setInteractionFormData({
                    interaction_type: '',
                    date: new Date().toISOString().split('T')[0],
                    subject: '',
                    notes: '',
                    next_followup_date: ''
                });
                setShowInteractionForm(false);
                fetchAccountInteractions(); // Refresh the interactions list
            }
        } catch (error) {
            console.error('Error creating interaction:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: error.response?.data?.message || 'Failed to add interaction. Please try again.'
            });
        } finally {
            setSubmittingInteraction(false);
        }
    };

    // Delete Account
    const handleDeleteAccount = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `You won't be able to revert this! This will also affect ${contacts.length} associated contact(s).`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/accounts/${id}`);
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Account has been deleted successfully.',
                    confirmButtonText: 'OK'
                }).then(() => {
                    navigate('/account');
                });
            } catch (error) {
                console.error('Error deleting account:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'Failed to delete account',
                    confirmButtonText: 'OK'
                });
            }
        }
    };

    useEffect(() => {
        if (id) {
            fetchAccountDetails();
            fetchAccountContacts();
            fetchAccountInteractions();
            fetchDocs();
            fetchPayments();
        }
    }, [id]);

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !account) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="alert alert-danger" role="alert">
                        {error || "Account not found"}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content container-fluid">
                
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Account Details</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <button 
                                            className="btn btn-secondary me-2"
                                            onClick={() => navigate('/account')}
                                        >
                                            <i className="fa fa-arrow-left me-2"></i>Back to Accounts
                                        </button>
                                    </li>
                                    <li>
                                        <button 
                                            className="btn btn-primary me-2"
                                            onClick={() => navigate('/Add-account', { state: { account, isEdit: true } })}
                                        >
                                            <i className="fa fa-edit me-2"></i>Edit Account
                                        </button>
                                    </li>
                                    <li>
                                        <button 
                                            className="btn btn-success me-2"
                                            onClick={() => navigate('/add-contact', { state: { account_id: account.id, account_name: account.company_name } })}
                                        >
                                            <i className="fa fa-user-plus me-2"></i>Add Contact
                                        </button>
                                    </li>
                                    <li>
                                        <button 
                                            className="btn btn-danger"
                                            onClick={handleDeleteAccount}
                                        >
                                            <i className="fa fa-trash me-2"></i>Delete Account
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    {/* /Page Header */}

                    {/* Basic Account Information */}
                    <div className="card customer-details-group">
                        <div className="card-header">
                            <h6 className="card-title mb-0">
                                <i className="fe fe-briefcase me-2"></i>Company Information
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="row align-items-center">					
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-briefcase"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Company Name</h6>
                                                <p>{account.company_name || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div> 
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-globe"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Website</h6>
                                                <p>
                                                    {account.website ? (
                                                        <a 
                                                            href={account.website.startsWith('http') ? account.website : `https://${account.website}`} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-primary"
                                                        >
                                                            {account.website}
                                                        </a>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-layers"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Industry</h6>
                                                <p>{account.industry || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-layers"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Sub Industry</h6>
                                                <p>{account.sub_industry || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="card customer-details-group">
                        <div className="card-header">
                            <h6 className="card-title mb-0">
                                <i className="fe fe-phone me-2"></i>Contact Information
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="row align-items-center">
                                <div className="col-xl-6 col-lg-6 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-phone"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Phone Number</h6>
                                                <p>
                                                    {account.phone ? (
                                                        <a href={`tel:${account.phone}`} className="text-primary">
                                                            {account.phone}
                                                        </a>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-6 col-lg-6 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-mail"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Email Address</h6>
                                                <p>
                                                    {account.email ? (
                                                        <a href={`mailto:${account.email}`} className="text-primary">
                                                            {account.email}
                                                        </a>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Address Information */}
                    <div className="row">
                        {/* Billing Address */}
                        <div className="col-xl-6 col-lg-6 col-md-12 col-12">
                            <div className="card customer-details-group">
                                <div className="card-header">
                                    <h6 className="card-title mb-0">
                                        <i className="fe fe-map-pin me-2"></i>Billing Address
                                    </h6>
                                </div>
                                <div className="card-body">
                                    <div className="customer-details">
                                        <div className="customer-details-cont">
                                            {account.billing_address_line1 || account.billing_address_line2 || 
                                             account.billing_city || account.billing_state || account.billing_country ? (
                                                <div>
                                                    {account.billing_address_line1 && <p className="mb-1">{account.billing_address_line1}</p>}
                                                    {account.billing_address_line2 && <p className="mb-1">{account.billing_address_line2}</p>}
                                                    <div className="d-flex flex-wrap">
                                                        {account.billing_city?.name && <span className="me-2">{account.billing_city.name},</span>}
                                                        {account.billing_state?.name && <span className="me-2">{account.billing_state.name}</span>}
                                                        {account.billing_zip && <span className="me-2">- {account.billing_zip}</span>}
                                                    </div>
                                                    {account.billing_country?.name && <p className="mb-0 text-muted">{account.billing_country.name}</p>}
                                                </div>
                                            ) : (
                                                <p className="text-muted">No billing address available</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Shipping Address */}
                        <div className="col-xl-6 col-lg-6 col-md-12 col-12">
                            <div className="card customer-details-group">
                                <div className="card-header">
                                    <h6 className="card-title mb-0">
                                        <i className="fe fe-truck me-2"></i>Shipping Address
                                    </h6>
                                </div>
                                <div className="card-body">
                                    <div className="customer-details">
                                        <div className="customer-details-cont">
                                            {account.shipping_address_line1 || account.shipping_address_line2 || 
                                             account.shipping_city || account.shipping_state || account.shipping_country ? (
                                                <div>
                                                    {account.shipping_address_line1 && <p className="mb-1">{account.shipping_address_line1}</p>}
                                                    {account.shipping_address_line2 && <p className="mb-1">{account.shipping_address_line2}</p>}
                                                    <div className="d-flex flex-wrap">
                                                        {account.shipping_city?.name && <span className="me-2">{account.shipping_city.name},</span>}
                                                        {account.shipping_state?.name && <span className="me-2">{account.shipping_state.name}</span>}
                                                        {account.shipping_zip && <span className="me-2">- {account.shipping_zip}</span>}
                                                    </div>
                                                    {account.shipping_country?.name && <p className="mb-0 text-muted">{account.shipping_country.name}</p>}
                                                </div>
                                            ) : (
                                                <p className="text-muted">No shipping address available</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional Information */}
                    <div className="card customer-details-group">
                        <div className="card-header">
                            <h6 className="card-title mb-0">
                                <i className="fe fe-info me-2"></i>Additional Information
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="row align-items-center">
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-activity"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Status</h6>
                                                <p>
                                                    <span className={`badge ${account.isactive ? 'bg-success' : 'bg-danger'}`}>
                                                        {account.isactive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-calendar"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Created Date</h6>
                                                <p>
                                                    {account.created_at ? 
                                                        new Date(account.created_at).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        }) : 'N/A'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-edit"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Last Updated</h6>
                                                <p>
                                                    {account.updated_at ? 
                                                        new Date(account.updated_at).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        }) : 'N/A'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-users"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Contacts Count</h6>
                                                <p>
                                                    <span className="badge bg-info">
                                                        {contacts.length} Contact{contacts.length !== 1 ? 's' : ''}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Created By */}
                                {account.createdBy && (
                                    <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                        <div className="customer-details">
                                            <div className="d-flex align-items-center">
                                                <span className="customer-widget-icon d-inline-flex">
                                                    <i className="fe fe-user-plus"></i>
                                                </span>
                                                <div className="customer-details-cont">
                                                    <h6>Created By</h6>
                                                    <p>{account.createdBy.first_name} {account.createdBy.last_name}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Updated By */}
                                {account.updatedBy && (
                                    <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                        <div className="customer-details">
                                            <div className="d-flex align-items-center">
                                                <span className="customer-widget-icon d-inline-flex">
                                                    <i className="fe fe-user-check"></i>
                                                </span>
                                                <div className="customer-details-cont">
                                                    <h6>Updated By</h6>
                                                    <p>{account.updatedBy.first_name} {account.updatedBy.last_name}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Notes */}
                                {account.notes && (
                                    <div className="col-12">
                                        <div className="customer-details">
                                            <div className="d-flex align-items-start">
                                                <span className="customer-widget-icon d-inline-flex">
                                                    <i className="fe fe-file-text"></i>
                                                </span>
                                                <div className="customer-details-cont">
                                                    <h6>Notes</h6>
                                                    <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {account.notes}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Finance Documents: Quotations / Invoices ── */}
                    {(() => {
                        // ── Build invoice outstanding map with waterfall for unlinked payments ──
                        // 1. Sum payment records that are explicitly linked to an invoice
                        const linkedPerInvoice = payments.reduce((acc, p) => {
                            if (p.quotation_id != null) {
                                const k = String(p.quotation_id);
                                acc[k] = (acc[k] || 0) + parseFloat(p.amount || 0);
                            }
                            return acc;
                        }, {});

                        // 2. Sum payment records with NO linked document
                        let remainingUnlinked = payments
                            .filter(p => p.quotation_id == null)
                            .reduce((s, p) => s + parseFloat(p.amount || 0), 0);

                        // 3. Apply unlinked payments to invoices (oldest date first — waterfall)
                        const invoiceOutstanding = {};
                        [...invoices]
                            .sort((a, b) => (a.date_str || '').localeCompare(b.date_str || ''))
                            .forEach(inv => {
                                const linked     = linkedPerInvoice[String(inv.id)] || 0;
                                const balance    = parseFloat(inv.balance || 0);
                                const afterLinked = Math.max(0, balance - linked);
                                const takeUnlinked = Math.min(remainingUnlinked, afterLinked);
                                remainingUnlinked -= takeUnlinked;
                                invoiceOutstanding[String(inv.id)] = Math.max(0, afterLinked - takeUnlinked);
                            });

                        return [
                        { label: 'Quotations', docs: quotations, type: 'quotation', icon: 'fa-file-alt',            color: '#0a1f5c', bg: '#e0e7ff' },
                        { label: 'Invoices',   docs: invoices,   type: 'invoice',   icon: 'fa-file-invoice-dollar', color: '#7c2d12', bg: '#fee2e2' },
                    ].map(({ label, docs, type, icon, color, bg }) => {
                        const isInvoice = type === 'invoice';

                        return (
                        <div className="card mt-4" key={type}>
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">
                                    <i className={`fas ${icon} me-2`}></i>{label}
                                    <span className="badge ms-2" style={{ background: bg, color }}>{docs.length}</span>
                                </h5>
                                <button className="btn btn-primary btn-sm" onClick={() => navigate('/quotation')}>
                                    <i className="fas fa-plus me-1"></i>Create {label.slice(0, -1)}
                                </button>
                            </div>
                            <div className="card-body p-0">
                                {docs.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        <i className={`fas ${icon}`} style={{ fontSize: '2rem', opacity: .2 }}></i>
                                        <p className="mt-2 mb-0 small">No {label.toLowerCase()} for this account</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>Document No.</th>
                                                    <th>Date</th>
                                                    <th>Total</th>
                                                    {isInvoice && <th>Advance Paid</th>}
                                                    {isInvoice && <th>Outstanding</th>}
                                                    {isInvoice && <th>Status</th>}
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {docs.map(q => {
                                                    // For invoices: use waterfall outstanding (linked + unlinked payments applied)
                                                    const outstanding = isInvoice ? (invoiceOutstanding[String(q.id)] ?? 0) : 0;
                                                    const paidFromRecords = isInvoice ? (linkedPerInvoice[String(q.id)] || 0) : 0;
                                                    const status = outstanding <= 0 ? 'paid'
                                                                 : paidFromRecords > 0 ? 'partial' : 'unpaid';
                                                    return (
                                                        <tr key={q.id}>
                                                            <td>
                                                                <span className="badge" style={{ background: bg, color, fontSize: 13, fontWeight: 700 }}>
                                                                    {q.number}
                                                                </span>
                                                            </td>
                                                            <td>{fmtDate(q.date_str)}</td>
                                                            <td><strong>{fmt(q.subtotal)}</strong></td>
                                                            {isInvoice && (
                                                                <td>
                                                                    {parseFloat(q.advance || 0) > 0
                                                                        ? <span className="text-success fw-bold">{fmt(q.advance)}</span>
                                                                        : <span className="text-muted">—</span>}
                                                                </td>
                                                            )}
                                                            {isInvoice && (
                                                                <td>
                                                                    {outstanding > 0
                                                                        ? <strong className="text-danger">{fmt(outstanding)}</strong>
                                                                        : <span className="text-success">—</span>}
                                                                </td>
                                                            )}
                                                            {isInvoice && (
                                                                <td>
                                                                    {status === 'paid'    && <span className="badge bg-success">Paid</span>}
                                                                    {status === 'partial' && <span className="badge bg-warning text-dark">Partial</span>}
                                                                    {status === 'unpaid'  && <span className="badge bg-danger">Unpaid</span>}
                                                                </td>
                                                            )}
                                                            <td>
                                                                <div className="d-flex gap-1">
                                                                    <button className="btn btn-sm btn-outline-primary"
                                                                        title="View document"
                                                                        onClick={() => setViewingDoc(q)}>
                                                                        <i className="fas fa-eye"></i>
                                                                    </button>
                                                                    {isInvoice && outstanding > 0 && (
                                                                        <button className="btn btn-sm btn-success"
                                                                            title={`Record payment · Outstanding: ${fmt(outstanding)}`}
                                                                            onClick={() => {
                                                                                setPaymentForm(f => ({
                                                                                    ...f,
                                                                                    quotation_id: String(q.id),
                                                                                    amount: Math.round(outstanding),
                                                                                }));
                                                                                setShowPaymentForm(true);
                                                                                setTimeout(() => paymentFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
                                                                            }}>
                                                                            <i className="fas fa-plus me-1"></i>Pay
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                        );
                    }); })()}

                    {/* ── Payment Records ── */}
                    {(() => {
                        // All documents across types — for dropdown + linked-doc lookup
                        const allDocs = [...quotations, ...invoices];

                        // Paid per document (for showing installment count in the dropdown)
                        const paidPerDoc = payments.reduce((acc, p) => {
                            if (p.quotation_id != null) {
                                const k = String(p.quotation_id);
                                acc[k] = (acc[k] || 0) + parseFloat(p.amount || 0);
                            }
                            return acc;
                        }, {});

                        const installmentsPerDoc = payments.reduce((acc, p) => {
                            if (p.quotation_id != null) {
                                const k = String(p.quotation_id);
                                acc[k] = (acc[k] || 0) + 1;
                            }
                            return acc;
                        }, {});

                        // Currently selected doc in the payment form
                        const selectedDoc = allDocs.find(d => String(d.id) === String(paymentForm.quotation_id));
                        const selectedDue = selectedDoc
                            ? (parseFloat(selectedDoc.balance) > 0 ? parseFloat(selectedDoc.balance) : parseFloat(selectedDoc.subtotal || 0))
                            : 0;
                        const selectedPaid = selectedDoc ? (paidPerDoc[String(selectedDoc.id)] || 0) : 0;
                        const selectedOutstanding = Math.max(0, selectedDue - selectedPaid);

                        const totalReceived = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);

                        return (
                        <div className="card mt-4" ref={paymentFormRef}>
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">
                                    <i className="fas fa-hand-holding-usd me-2"></i>Payment Records
                                    {payments.length > 0 && (
                                        <span className="ms-3 text-success fw-bold">
                                            Total Received: {fmt(totalReceived)}
                                        </span>
                                    )}
                                </h5>
                                <button className="btn btn-success btn-sm" onClick={() => setShowPaymentForm(v => !v)}>
                                    <i className="fas fa-plus me-1"></i>Add Payment
                                </button>
                            </div>

                            {showPaymentForm && (
                                <div className="card-body border-bottom bg-light">
                                    <h6 className="mb-3">Record Payment</h6>
                                    <form onSubmit={savePayment}>
                                        <div className="row g-3">
                                            {/* Linked Document — full width on top so outstanding info is visible */}
                                            <div className="col-md-4">
                                                <label className="form-label">Linked Document</label>
                                                <select className="form-select"
                                                    value={paymentForm.quotation_id}
                                                    onChange={e => {
                                                        const docId = e.target.value;
                                                        const doc   = allDocs.find(d => String(d.id) === docId);
                                                        const due   = doc ? (parseFloat(doc.balance) > 0 ? parseFloat(doc.balance) : parseFloat(doc.subtotal || 0)) : 0;
                                                        const paid  = doc ? (paidPerDoc[String(doc.id)] || 0) : 0;
                                                        const rem   = Math.max(0, due - paid);
                                                        setPaymentForm(f => ({ ...f, quotation_id: docId, amount: rem > 0 ? Math.round(rem) : f.amount }));
                                                    }}>
                                                    <option value="">— not linked to a document —</option>
                                                    {allDocs.map(d => {
                                                        const due  = parseFloat(d.balance) > 0 ? parseFloat(d.balance) : parseFloat(d.subtotal || 0);
                                                        const paid = paidPerDoc[String(d.id)] || 0;
                                                        const rem  = Math.max(0, due - paid);
                                                        return (
                                                            <option key={d.id} value={d.id}>
                                                                {d.number} — {rem > 0 ? `Outstanding: ₹${Math.round(rem).toLocaleString('en-IN')}` : 'Fully Paid'}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                {/* Outstanding info banner */}
                                                {selectedDoc && (
                                                    <div className={`mt-2 p-2 rounded small d-flex gap-3 ${selectedOutstanding <= 0 ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-10'}`}>
                                                        <span><b>Total:</b> {fmt(selectedDue)}</span>
                                                        <span><b>Paid so far:</b> {fmt(selectedPaid)}</span>
                                                        <span className={selectedOutstanding > 0 ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                                                            <b>Outstanding:</b> {fmt(selectedOutstanding)}
                                                        </span>
                                                        {installmentsPerDoc[String(selectedDoc.id)] > 0 && (
                                                            <span className="text-muted">
                                                                ({installmentsPerDoc[String(selectedDoc.id)]} payment{installmentsPerDoc[String(selectedDoc.id)] > 1 ? 's' : ''} recorded)
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="col-md-2">
                                                <label className="form-label">Amount (₹) <span className="text-danger">*</span></label>
                                                <input type="number" min="0" step="1" className="form-control" placeholder="0"
                                                    value={paymentForm.amount}
                                                    onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))} required />
                                                {selectedOutstanding > 0 && parseFloat(paymentForm.amount) !== selectedOutstanding && (
                                                    <small className="text-muted">
                                                        Full outstanding: {fmt(selectedOutstanding)}
                                                        <button type="button" className="btn btn-link btn-sm p-0 ms-1"
                                                            onClick={() => setPaymentForm(f => ({ ...f, amount: Math.round(selectedOutstanding) }))}>
                                                            Use
                                                        </button>
                                                    </small>
                                                )}
                                            </div>

                                            <div className="col-md-2">
                                                <label className="form-label">Date <span className="text-danger">*</span></label>
                                                <input type="date" className="form-control" value={paymentForm.date}
                                                    onChange={e => setPaymentForm(f => ({ ...f, date: e.target.value }))} required />
                                            </div>

                                            <div className="col-md-2">
                                                <label className="form-label">Method</label>
                                                <select className="form-select" value={paymentForm.payment_method}
                                                    onChange={e => setPaymentForm(f => ({ ...f, payment_method: e.target.value }))}>
                                                    {['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'Other'].map(m => (
                                                        <option key={m} value={m}>{m}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="col-md-2">
                                                <label className="form-label">Reference / UTR</label>
                                                <input type="text" className="form-control" placeholder="Transaction ref"
                                                    value={paymentForm.reference_no}
                                                    onChange={e => setPaymentForm(f => ({ ...f, reference_no: e.target.value }))} />
                                            </div>

                                            <div className="col-md-2">
                                                <label className="form-label">Notes</label>
                                                <input type="text" className="form-control" placeholder="Optional"
                                                    value={paymentForm.notes}
                                                    onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))} />
                                            </div>

                                            <div className="col-12 d-flex gap-2">
                                                <button type="submit" className="btn btn-success" disabled={savingPayment}>
                                                    {savingPayment
                                                        ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</>
                                                        : <><i className="fas fa-save me-1"></i>Save Payment</>}
                                                </button>
                                                <button type="button" className="btn btn-secondary"
                                                    onClick={() => {
                                                        setShowPaymentForm(false);
                                                        setPaymentForm({ date: new Date().toISOString().split('T')[0], amount: '', payment_method: 'UPI', reference_no: '', notes: '', quotation_id: '' });
                                                    }}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            )}

                            <div className="card-body p-0">
                                {payments.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        <i className="fas fa-hand-holding-usd" style={{ fontSize: '2rem', opacity: .2 }}></i>
                                        <p className="mt-2 mb-0 small">No payment records yet</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Date</th>
                                                    <th>Amount</th>
                                                    <th>Method</th>
                                                    <th>Reference</th>
                                                    <th>Linked Document</th>
                                                    <th>Notes</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {payments.map((p, idx) => {
                                                    const linkedDoc = allDocs.find(d => String(d.id) === String(p.quotation_id));
                                                    const docBadgeStyle = linkedDoc
                                                        ? linkedDoc.type === 'invoice'
                                                            ? { background: '#fee2e2', color: '#7c2d12' }
                                                            : { background: '#e0e7ff', color: '#0a1f5c' }
                                                        : null;
                                                    return (
                                                        <tr key={p.id}>
                                                            <td className="text-muted small">{idx + 1}</td>
                                                            <td>{fmtDate(p.date)}</td>
                                                            <td><strong className="text-success">{fmt(p.amount)}</strong></td>
                                                            <td>
                                                                <span className={`badge ${
                                                                    p.payment_method === 'Cash' ? 'bg-success' :
                                                                    p.payment_method === 'UPI'  ? 'bg-primary' :
                                                                    p.payment_method === 'Cheque' ? 'bg-warning text-dark' :
                                                                    'bg-secondary'}`}>
                                                                    {p.payment_method}
                                                                </span>
                                                            </td>
                                                            <td>{p.reference_no || <span className="text-muted">—</span>}</td>
                                                            <td>
                                                                {linkedDoc
                                                                    ? <span className="badge" style={docBadgeStyle}>{linkedDoc.number}</span>
                                                                    : <span className="text-muted">—</span>}
                                                            </td>
                                                            <td>{p.notes || <span className="text-muted">—</span>}</td>
                                                            <td>
                                                                <button className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => deletePayment(p.id)}>
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot>
                                                <tr className="table-light">
                                                    <td colSpan={2}><strong>Total Received</strong></td>
                                                    <td colSpan={6}>
                                                        <strong className="text-success" style={{ fontSize: 15 }}>
                                                            {fmt(totalReceived)}
                                                        </strong>
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                        );
                    })()}

                    {/* Account Interactions Section */}
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h6 className="card-title mb-0">
                                <i className="fe fe-message-circle me-2"></i>Account Interactions ({interactions.length})
                            </h6>
                            <button 
                                className="btn btn-sm btn-primary"
                                onClick={() => setShowInteractionForm(!showInteractionForm)}
                            >
                                <i className="fa fa-plus me-1"></i>Add Interaction
                            </button>
                        </div>

                        {/* Interaction Form */}
                        {showInteractionForm && (
                            <div className="card-body border-bottom">
                                <form onSubmit={handleSubmitInteraction}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Interaction Type <span className="text-danger">*</span></label>
                                            <select
                                                className="form-select"
                                                name="interaction_type"
                                                value={interactionFormData.interaction_type}
                                                onChange={handleInteractionInputChange}
                                                required
                                            >
                                                <option value="">Select Type</option>
                                                <option value="call">Phone Call</option>
                                                <option value="email">Email</option>
                                                <option value="meeting">Meeting</option>
                                                <option value="demo">Demo</option>
                                                <option value="proposal">Proposal</option>
                                                <option value="follow_up">Follow Up</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Date <span className="text-danger">*</span></label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="date"
                                                value={interactionFormData.date}
                                                onChange={handleInteractionInputChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Subject <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="subject"
                                                value={interactionFormData.subject}
                                                onChange={handleInteractionInputChange}
                                                placeholder="Enter interaction subject"
                                                required
                                            />
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Notes</label>
                                            <textarea
                                                className="form-control"
                                                name="notes"
                                                rows="3"
                                                value={interactionFormData.notes}
                                                onChange={handleInteractionInputChange}
                                                placeholder="Enter interaction details..."
                                            ></textarea>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Next Follow-up Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="next_followup_date"
                                                value={interactionFormData.next_followup_date}
                                                onChange={handleInteractionInputChange}
                                            />
                                        </div>
                                        <div className="col-12">
                                            <button 
                                                type="submit" 
                                                className="btn btn-success me-2"
                                                disabled={submittingInteraction}
                                            >
                                                {submittingInteraction ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fa fa-check me-2"></i>Save Interaction
                                                    </>
                                                )}
                                            </button>
                                            <button 
                                                type="button" 
                                                className="btn btn-secondary"
                                                onClick={() => {
                                                    setShowInteractionForm(false);
                                                    setInteractionFormData({
                                                        interaction_type: '',
                                                        date: new Date().toISOString().split('T')[0],
                                                        subject: '',
                                                        note: '',
                                                        next_followup_at: ''
                                                    });
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Interactions List */}
                        <div className="card-body">
                            {interactions.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead className="thead-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Type</th>
                                                <th>Subject</th>
                                                <th>Date</th>
                                                <th>Notes</th>
                                                <th>Next Follow-up</th>
                                                <th>Created</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {interactions.map((interaction, index) => (
                                                <tr key={interaction.id}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        <span className={`badge ${
                                                            interaction.interaction_type === 'call' ? 'bg-primary' :
                                                            interaction.interaction_type === 'email' ? 'bg-info' :
                                                            interaction.interaction_type === 'meeting' ? 'bg-success' :
                                                            interaction.interaction_type === 'demo' ? 'bg-warning' :
                                                            interaction.interaction_type === 'proposal' ? 'bg-secondary' :
                                                            interaction.interaction_type === 'follow_up' ? 'bg-dark' :
                                                            'bg-light text-dark'
                                                        }`}>
                                                            {interaction.interaction_type.charAt(0).toUpperCase() + interaction.interaction_type.slice(1).replace('_', ' ')}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <strong>{interaction.subject || 'No Subject'}</strong>
                                                    </td>
                                                    <td>
                                                        {new Date(interaction.date).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric'
                                                        })}
                                                    </td>
                                                    <td>
                                                        {interaction.note ? (
                                                            <span title={interaction.note}>
                                                                {interaction.note.length > 50 
                                                                    ? interaction.note.substring(0, 50) + '...' 
                                                                    : interaction.note
                                                                }
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">No notes</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {interaction.next_followup_at ? (
                                                            <span className={`badge ${
                                                                new Date(interaction.next_followup_at) < new Date() ? 'bg-danger' :
                                                                new Date(interaction.next_followup_at).toDateString() === new Date().toDateString() ? 'bg-warning' :
                                                                'bg-success'
                                                            }`}>
                                                                {new Date(interaction.next_followup_at).toLocaleDateString('en-US', {
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                    year: 'numeric'
                                                                })}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {interaction.created_at ? 
                                                            new Date(interaction.created_at).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            }) : 'N/A'
                                                        }
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="fe fe-message-circle text-muted" style={{ fontSize: '48px' }}></i>
                                    <h6 className="text-muted mt-2">No interactions found for this account</h6>
                                    <p className="text-muted">Add an interaction to track your communications</p>
                                    <button 
                                        className="btn btn-primary"
                                        onClick={() => setShowInteractionForm(true)}
                                    >
                                        <i className="fa fa-plus me-2"></i>Add First Interaction
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Associated Contacts */}
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h6 className="card-title mb-0">
                                <i className="fe fe-users me-2"></i>Associated Contacts ({contacts.length})
                            </h6>
                            <button 
                                className="btn btn-sm btn-primary"
                                onClick={() => navigate('/add-contact', { state: { account_id: account.id, account_name: account.company_name } })}
                            >
                                <i className="fa fa-plus me-1"></i>Add New Contact
                            </button>
                        </div>
                        <div className="card-body">
                            {contacts.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead className="thead-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>Designation</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {contacts.map((contact, index) => (
                                                <tr key={contact.id}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        <span 
                                                            style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                                            onClick={() => navigate(`/contact-details/${contact.id}`)}
                                                            title="Click to view contact details"
                                                        >
                                                            {contact.first_name} {contact.last_name}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {contact.email ? (
                                                            <a href={`mailto:${contact.email}`} className="text-primary">
                                                                {contact.email}
                                                            </a>
                                                        ) : (
                                                            'N/A'
                                                        )}
                                                    </td>
                                                    <td>
                                                        {contact.phone ? (
                                                            <a href={`tel:${contact.phone}`} className="text-primary">
                                                                {contact.phone}
                                                            </a>
                                                        ) : (
                                                            'N/A'
                                                        )}
                                                    </td>
                                                    <td>{contact.designation || 'N/A'}</td>
                                                    <td>
                                                        <span className={`badge ${contact.isactive ? 'bg-success' : 'bg-danger'}`}>
                                                            {contact.isactive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex">
                                                            <button 
                                                                className="btn btn-sm btn-outline-primary me-1"
                                                                onClick={() => navigate(`/contact-details/${contact.id}`)}
                                                                title="View Contact Details"
                                                            >
                                                                <i className="fa fa-eye"></i>
                                                            </button>
                                                            <button 
                                                                className="btn btn-sm btn-outline-success"
                                                                onClick={() => navigate('/add-contact', { state: { contact, isEdit: true } })}
                                                                title="Edit Contact"
                                                            >
                                                                <i className="fa fa-edit"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="fe fe-users text-muted" style={{ fontSize: '48px' }}></i>
                                    <h6 className="text-muted mt-2">No contacts found for this account</h6>
                                    <p className="text-muted">Add a contact to get started</p>
                                    <button 
                                        className="btn btn-primary"
                                        onClick={() => navigate('/add-contact', { state: { account_id: account.id, account_name: account.company_name } })}
                                    >
                                        <i className="fa fa-plus me-2"></i>Add First Contact
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
            {/* /Page Wrapper */}

            <DocViewModal doc={viewingDoc} onClose={() => setViewingDoc(null)} />
        </div>
    )
}

export default Accountdetails;